import React, { useEffect, useState } from "react";
import DashboardHeader from "../components/Other/DashboardHeader";
import PageHeaderTitle from "../components/pages/PageHeaderTitle";
import PageContainer from "../components/pages/PageContainer";
import { useLocation, useOutletContext } from "react-router-dom";
import { isLeagueChatPath } from "../utils/leaguePath";
import SettingsPageHeading from "../components/pages/SettingsPageHeading";
import ChatsDesktop from "../components/Chats/ChatsDesktop";
import ChatsMobileList from "../components/Chats/mobile/ChatsMobileList";

const ChatPage = () => {
  // AuthLayout passes [sidebarToggle]; the Contest layout passes an OBJECT
  // ({ search, handleChange }). Array-destructuring an object is a TypeError, so
  // this screen crashed when mounted inside a league at /contest/<slug>/chat.
  const outletContext = useOutletContext();
  const sidebarToggle = Array.isArray(outletContext)
    ? outletContext[0]
    : outletContext?.sidebarToggle;
  // ≥1024 gets the two-panel screen (list + conversation in one card); below
  // that the list is a screen of its own that pushes to the thread.
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1024);
  const location = useLocation();

  // Client Rev 2, desktop: "Remove only the outer wrapper, including its border
  // and shadow. Do not remove the border around the actual Notifications card or
  // Chats panel. Center the remaining content within the available content
  // area… use the right edge of the desktop sidebar as the left boundary."
  //
  // Both were the same defect. Opened from inside a league this screen mounts at
  // /contest/<slug>/chat, which is a CHILD of the Contest layout — and Contest
  // already renders a DashboardHeader and a PageContainer around its <Outlet/>.
  // Rendering our own on top of them produced a second PageContainer nested in
  // the first, so `md:ml-64` was applied TWICE: measured at 1536px wide, the
  // content column sat at x=573 instead of x=448, i.e. 125px right of the centre
  // of the area beside the sidebar — the empty left gutter the client marked.
  // The nested container's `shadow-md` is the outer border/shadow they want
  // gone; the bordered Chats panel is ChatsDesktop's own and is untouched.
  //
  // The duplicate header was the other half: `position: fixed` with no `left`
  // resolves to the element's STATIC position, so the second header started
  // 264px in and its right-hand glyphs landed at x=1742 — off a 1536px screen —
  // while its opaque background covered the correctly placed ones underneath.
  // That is why the top right looked empty on both screens.
  //
  // So inside a league this screen renders neither: the shell it is hosted in
  // owns the header and the container, exactly like the league's other sub-pages
  // (Portfolio, League details, League settings). The main app's own /chat is
  // untouched — it has no layout above it, so it still brings its own.
  const hostedInLeague = isLeagueChatPath(location.pathname);

  useEffect(() => {
    const onResize = () => setIsDesktop(window.innerWidth >= 1024);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const body = (
    <div className="bg-white">
          <main className="h-full justify-center pt-4 md:pt-10">
            {/* Title sits OUTSIDE the card, aligned to the content margin.
                Desktop = bordered card (owned by ChatsDesktop, which needs to
                clip its two scrolling panels); mobile = bare, full width. */}
            <div className="mx-auto mb-12 w-full lg:max-w-5xl">
              {/* Rows carry their own px-4, so the heading matches it rather
                  than the wrapper padding the whole list a second time. */}
              <SettingsPageHeading
                title="Chats"
                className="mb-3 px-4 md:mb-4 lg:px-0"
              />
              {isDesktop ? <ChatsDesktop /> : <ChatsMobileList />}
            </div>
          </main>
    </div>
  );

  return (
    <>
      {!hostedInLeague && <DashboardHeader toggle={sidebarToggle} />}
      {/* House rule: title "Chats" — no desktop title text / grey line. The
          Contest layout renders no title of its own, so this stays on both. */}
      <PageHeaderTitle title="Chats" showDesktopTitle={false} />

      {hostedInLeague ? body : <PageContainer>{body}</PageContainer>}
    </>
  );
};

export default ChatPage;
